import { sentence } from '../core/words.js';
import { createBasketball, CAN, COURT, ORIGIN } from '../sim/classes/basketball.js';
import { createRefresh, LANE, TRAY } from '../sim/classes/refresh.js';
import { createVacuum, ROOM } from '../sim/classes/vacuum.js';
import { createHands, PASS, STATION, CHIMIS } from '../sim/classes/hands.js';

/**
 * The class screen.
 *
 * One panel for all three, because all three are the same thing from here: a
 * pure simulation that wants a pointer and a clock, a canvas, and a line at the
 * end saying what you scored and whether that was a pass. Everything that is
 * actually different between them lives in `sim/classes/`, and what is left in
 * this file is drawing.
 *
 * It is a canvas rather than DOM because two of the three are continuous — a
 * ball in the air, a tray under falling crockery — and a div per falling fork
 * is a layout thrash sixty times a second for no benefit. The third would have
 * been fine either way and being the odd one out would have been worse.
 *
 * Deliberately not pointer-locked. A class is a screen you play with the mouse
 * the way you read the board with the mouse, and the world is frozen behind it.
 */

const INK = {
  back: '#141821',
  floor: '#1c212c',
  line: '#333a49',
  edge: '#4a5365',
  chalk: '#8d94a6',
  bright: '#e6e8ee',
  good: '#6fbf88',
  warn: '#d8b45e',
  bad: '#e0685f',
};

/** A colour per thing off the pass, so a loaded tray is readable at a glance. */
const KIND = {
  cup: '#8fb3d9',
  plate: '#d9d2c4',
  fork: '#9aa2b4',
  spoon: '#9aa2b4',
  napkin: '#c8a15a',
};

const GAMES = {
  basketball: createBasketball,
  refresh: createRefresh,
  vacuum: createVacuum,
  hands: createHands,
};

export function createClassPanel({ parent, onFinish, onLeave }) {
  const root = document.createElement('div');
  root.className = 'class';
  root.hidden = true;

  const head = document.createElement('div');
  head.className = 'class-head';
  head.innerHTML = `<h2></h2><span class="class-sub"></span>
    <button class="class-leave" type="button">Leave  [Esc]</button>`;

  const canvas = document.createElement('canvas');
  canvas.className = 'class-canvas';
  canvas.width = 900;
  canvas.height = 560;

  const foot = document.createElement('div');
  foot.className = 'class-foot';

  const result = document.createElement('div');
  result.className = 'class-result';
  result.hidden = true;

  root.append(head, canvas, foot, result);
  parent.append(root);

  const context = canvas.getContext('2d');
  let game = null;
  let entry = null;
  let row = null;
  let finished = false;
  /** Pointer in canvas pixels. The games are handed their own units. */
  let pointer = { x: canvas.width / 2, y: canvas.height / 2 };
  /** Whether the pad has moved the pointer, so it is drawn. */
  let padCursor = false;
  /** A held as the mouse button, last frame, for its edges. */
  let padDown = false;

  function close({ finishing = false } = {}) {
    root.hidden = true;
    const was = { entry, row, game };
    game = null;
    entry = null;
    row = null;
    if (!finishing) onLeave?.(was);
  }

  head.querySelector('.class-leave').addEventListener('click', () => close());

  canvas.addEventListener('mousemove', (event) => {
    const box = canvas.getBoundingClientRect();
    pointer = {
      x: ((event.clientX - box.left) / box.width) * canvas.width,
      y: ((event.clientY - box.top) / box.height) * canvas.height,
    };
  });

  canvas.addEventListener('mousedown', () => {
    if (!game || finished) return;
    if (entry.game === 'basketball') game.flick();
    // Quick Hands is held rather than clicked: the reach takes three quarters
    // of a second and letting go part way is standing up straight again.
    if (entry.game === 'hands') game.hold(true);
  });

  const letGo = () => {
    if (game && !finished && entry.game === 'hands') game.hold(false);
  };
  canvas.addEventListener('mouseup', letGo);
  canvas.addEventListener('mouseleave', letGo);

  // --- drawing ---------------------------------------------------------------

  function frame() {
    context.fillStyle = INK.back;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }

  function drawBasketball() {
    const scale = canvas.width / COURT.width;
    const toX = (x) => x * scale;
    const toY = (y) => canvas.height - 60 - y * scale;

    context.fillStyle = INK.floor;
    context.fillRect(0, toY(0), canvas.width, canvas.height - toY(0));
    context.strokeStyle = INK.line;
    context.beginPath();
    context.moveTo(0, toY(0));
    context.lineTo(canvas.width, toY(0));
    context.stroke();

    /*
     * The arc the flick would throw, not a line to the cursor.
     *
     * The control is "the harder and higher you point, the harder and higher
     * it goes", and a dashed straight line says nothing about where that puts
     * the ball. Drawing the actual arc is what makes the throw learnable; what
     * it does not draw is where the can will have slid to by the time the ball
     * gets there, which is the part that is meant to be difficult.
     */
    const arc = game.ghost();
    context.strokeStyle = INK.edge;
    context.setLineDash([3, 6]);
    context.beginPath();
    context.moveTo(toX(arc[0].x), toY(arc[0].y));
    for (const point of arc.slice(1)) context.lineTo(toX(point.x), toY(point.y));
    context.stroke();
    context.setLineDash([]);
    // Where it would land, so a short throw is visibly short.
    const end = arc[arc.length - 1];
    context.strokeStyle = INK.edge;
    context.beginPath();
    context.arc(toX(end.x), toY(end.y), 4, 0, Math.PI * 2);
    context.stroke();

    // Whoever is doing the throwing: an arm, and a hand at the end of it.
    context.strokeStyle = INK.chalk;
    context.lineWidth = 3;
    context.beginPath();
    context.moveTo(toX(ORIGIN.x) - 10, toY(0));
    context.lineTo(toX(ORIGIN.x) - 10, toY(ORIGIN.y + 1.1));
    context.lineTo(toX(ORIGIN.x), toY(ORIGIN.y));
    context.stroke();
    context.lineWidth = 1;
    context.fillStyle = INK.chalk;
    context.beginPath();
    context.arc(toX(ORIGIN.x) - 10, toY(ORIGIN.y + 1.5), 7, 0, Math.PI * 2);
    context.fill();

    // The can, and the colleague sliding it about.
    const can = game.can;
    const w = can.mouth * scale;
    context.fillStyle = '#20252f';
    context.beginPath();
    context.moveTo(toX(can.x) - w / 2, toY(can.height));
    context.lineTo(toX(can.x) - w / 2 + 4, toY(0));
    context.lineTo(toX(can.x) + w / 2 - 4, toY(0));
    context.lineTo(toX(can.x) + w / 2, toY(can.height));
    context.closePath();
    context.fill();
    context.strokeStyle = INK.bright;
    context.lineWidth = 2;
    context.stroke();
    context.lineWidth = 1;
    // The mouth, which is the thing you are actually aiming at.
    context.strokeStyle = INK.good;
    context.lineWidth = 3;
    context.beginPath();
    context.moveTo(toX(can.x) - w / 2, toY(can.height));
    context.lineTo(toX(can.x) + w / 2, toY(can.height));
    context.stroke();
    context.lineWidth = 1;

    const ball = game.inFlight;
    if (ball) {
      context.fillStyle = INK.warn;
      context.beginPath();
      context.arc(toX(ball.x), toY(ball.y), 6, 0, Math.PI * 2);
      context.fill();
    }

    // One tick per shot taken, made or missed, so the round has a shape.
    for (let i = 0; i < game.log.length; i += 1) {
      context.fillStyle = game.log[i].made ? INK.good : INK.line;
      context.fillRect(24 + i * 14, 24, 9, 9);
    }
  }

  function drawRefresh() {
    const scale = canvas.width / LANE.width;
    const toX = (x) => canvas.width / 2 + x * scale;
    const toY = (y) => canvas.height - 70 - y * (canvas.height - 120) / LANE.top;

    context.strokeStyle = INK.line;
    context.beginPath();
    context.moveTo(0, toY(LANE.top));
    context.lineTo(canvas.width, toY(LANE.top));
    context.stroke();

    for (const item of game.falling) {
      const w = item.item.foot * scale;
      context.fillStyle = KIND[item.item.id] ?? INK.chalk;
      context.fillRect(toX(item.x) - w / 2, toY(item.y) - 7, w, 9);
    }

    // The tray, rotated by the tilt so the danger is a thing you can see.
    const tilt = game.tilt;
    context.save();
    context.translate(toX(game.trayX), toY(TRAY.y));
    context.rotate(tilt * 0.4);
    context.strokeStyle = Math.abs(tilt) > 0.6 ? INK.bad : INK.bright;
    context.lineWidth = 3;
    context.beginPath();
    context.moveTo((-TRAY.width / 2) * scale, 0);
    context.lineTo((TRAY.width / 2) * scale, 0);
    context.stroke();
    context.lineWidth = 1;
    /*
     * Stacked where they actually are, layer and all. They used to all be
     * drawn flat on the tray, which is a picture of a different game: the
     * whole question the player is answering is where the weight is, and a
     * tray drawn as a single row of things says the tray is never full.
     */
    for (const sitting of game.stack) {
      const w = sitting.item.foot * scale;
      context.fillStyle = KIND[sitting.item.id] ?? INK.edge;
      context.fillRect(sitting.offset * scale - w / 2, -11 - sitting.layer * 11, w, 9);
    }
    context.restore();

    // The spirit level, labelled, because a bar with no name is a mystery.
    context.fillStyle = INK.chalk;
    context.font = '11px ui-monospace, monospace';
    context.textAlign = 'center';
    context.fillText('LEVEL', canvas.width / 2, 18);
    context.textAlign = 'left';
    const barWidth = 260;
    const left = canvas.width / 2 - barWidth / 2;
    context.strokeStyle = INK.line;
    context.strokeRect(left, 22, barWidth, 10);
    const limit = Math.min(1, Math.abs(tilt) / row.tipAt);
    context.fillStyle = limit > 0.75 ? INK.bad : limit > 0.45 ? INK.warn : INK.good;
    const half = (tilt / row.tipAt) * (barWidth / 2);
    context.fillRect(
      canvas.width / 2 + Math.min(0, half),
      22,
      Math.min(barWidth / 2, Math.abs(half)),
      10
    );
  }

  function drawVacuum() {
    const scale = Math.min((canvas.width - 60) / ROOM.cols, (canvas.height - 110) / ROOM.rows);
    const ox = (canvas.width - ROOM.cols * scale) / 2;
    const oy = 60;
    const toX = (x) => ox + x * scale;
    const toY = (y) => oy + y * scale;

    context.fillStyle = INK.floor;
    context.fillRect(toX(0), toY(0), ROOM.cols * scale, ROOM.rows * scale);
    context.strokeStyle = INK.line;
    context.strokeRect(toX(0), toY(0), ROOM.cols * scale, ROOM.rows * scale);

    for (const table of game.tables) {
      context.fillStyle = '#242a36';
      context.fillRect(
        toX(table.x - table.half),
        toY(table.y - table.half),
        table.half * 2 * scale,
        table.half * 2 * scale
      );
      context.strokeStyle = INK.edge;
      context.strokeRect(
        toX(table.x - table.half),
        toY(table.y - table.half),
        table.half * 2 * scale,
        table.half * 2 * scale
      );
    }
    context.fillStyle = INK.edge;
    for (const leg of game.legs) {
      context.beginPath();
      context.arc(toX(leg.x), toY(leg.y), 3, 0, Math.PI * 2);
      context.fill();
    }

    for (const piece of game.trash) {
      if (piece.gone) continue;
      context.fillStyle = INK.warn;
      context.fillRect(toX(piece.x) - 3, toY(piece.y) - 3, 6, 6);
    }

    // The cord, every wrap of it, and the socket it comes out of.
    const cord = game.cord;
    context.strokeStyle = game.slack < 1.5 ? INK.bad : INK.chalk;
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(toX(cord[0].x), toY(cord[0].y));
    for (const point of cord.slice(1)) context.lineTo(toX(point.x), toY(point.y));
    context.stroke();
    context.lineWidth = 1;

    context.fillStyle = INK.bright;
    context.fillRect(toX(cord[0].x) - 5, toY(cord[0].y) - 5, 10, 10);

    const head2 = game.head;
    context.fillStyle = INK.good;
    context.beginPath();
    context.arc(toX(head2.x), toY(head2.y), 9, 0, Math.PI * 2);
    context.fill();
  }

  /**
   * The pass, from above: the counter, the tray, you, and the cones.
   *
   * The cones are the screen. Everything the player is reading is "is that
   * wedge over the tray", and it is drawn from the same numbers perception
   * measures against — the chef's range, the chef's half-angle and the chef's
   * yaw — rather than from a prettier approximation, so a wedge that looks
   * clear is clear.
   */
  function drawHands() {
    const scale = canvas.width / PASS.width;
    const toX = (x) => x * scale;
    const toZ = (z) => 40 + z * scale;

    // The far wall, the pass counter, and the near side you are standing on.
    context.fillStyle = INK.floor;
    context.fillRect(0, toZ(0), canvas.width, toZ(PASS.depth) - toZ(0));
    context.strokeStyle = INK.line;
    context.beginPath();
    context.moveTo(0, toZ(8));
    context.lineTo(canvas.width, toZ(8));
    context.stroke();

    for (const who of game.chefs) {
      // The cone, as perception has it: the range and the half-angle, drawn
      // round the yaw. Forward is +z, which is yaw zero.
      const half = (who.halfAngle * Math.PI) / 180;
      context.fillStyle = who.looking ? 'rgba(224,104,95,0.18)' : 'rgba(141,148,166,0.10)';
      context.beginPath();
      context.moveTo(toX(who.x), toZ(who.z));
      context.arc(
        toX(who.x),
        toZ(who.z),
        who.range * scale,
        Math.PI / 2 - who.yaw - half,
        Math.PI / 2 - who.yaw + half
      );
      context.closePath();
      context.fill();

      context.fillStyle = who.looking ? INK.bad : INK.chalk;
      context.beginPath();
      context.arc(toX(who.x), toZ(who.z), 8, 0, Math.PI * 2);
      context.fill();
    }

    // The tray, and how much of it is gone.
    context.strokeStyle = INK.bright;
    context.strokeRect(toX(CHIMIS.x) - 34, toZ(CHIMIS.z) - 12, 68, 24);
    context.fillStyle = INK.warn;
    const left = Math.max(0, row.chimis - game.taken) / row.chimis;
    context.fillRect(toX(CHIMIS.x) - 32, toZ(CHIMIS.z) - 10, 64 * left, 20);

    // You, and how far into a reach you are.
    context.fillStyle = INK.good;
    context.beginPath();
    context.arc(toX(STATION.x), toZ(STATION.z), 9, 0, Math.PI * 2);
    context.fill();
    if (game.reaching > 0) {
      context.strokeStyle = INK.good;
      context.lineWidth = 3;
      context.beginPath();
      context.arc(toX(STATION.x), toZ(STATION.z), 15, -Math.PI / 2, -Math.PI / 2 + game.reaching * Math.PI * 2);
      context.stroke();
      context.lineWidth = 1;
      // The arm, going in.
      context.strokeStyle = INK.good;
      context.beginPath();
      context.moveTo(toX(STATION.x), toZ(STATION.z));
      context.lineTo(toX(CHIMIS.x), toZ(CHIMIS.z) + 12);
      context.stroke();
    }

    /*
     * How sure the surest of them is, which is the thing that ends the round.
     *
     * Read straight off perception rather than off a counter this file keeps,
     * because it is perception that decides. The line on it is the level's own
     * `caughtAt`, so the bar is not a mood, it is the rule.
     */
    const barWidth = 300;
    const x0 = canvas.width / 2 - barWidth / 2;
    context.fillStyle = INK.chalk;
    context.font = '11px ui-monospace, monospace';
    context.textAlign = 'center';
    context.fillText('HOW SURE ANYBODY IS', canvas.width / 2, 18);
    context.textAlign = 'left';
    context.strokeStyle = INK.line;
    context.strokeRect(x0, 24, barWidth, 10);
    const heat = Math.min(1, game.heat);
    context.fillStyle = heat > row.caughtAt * 0.8 ? INK.bad : heat > row.caughtAt * 0.5 ? INK.warn : INK.good;
    context.fillRect(x0, 24, barWidth * heat, 10);
    context.strokeStyle = INK.bad;
    context.beginPath();
    context.moveTo(x0 + barWidth * row.caughtAt, 21);
    context.lineTo(x0 + barWidth * row.caughtAt, 37);
    context.stroke();
  }

  // --- the panel's own clock ---------------------------------------------------

  /**
   * One tick, driven by the game's frame even though the world is frozen.
   *
   * A class is not world time: the shift's minutes are charged once, at the end
   * of the sitting, by whoever opened the panel. What runs in here is real
   * seconds, which is the only clock a falling fork can be on.
   */
  /**
   * The cursor, in whatever units the game on screen thinks in.
   *
   * One conversion, used in both directions: `step` reads the pointer through
   * it and `cursorTo` writes the pointer through its inverse. Two copies of
   * this arithmetic would be two copies to get wrong, and the headless check
   * puts the cursor somewhere and then expects the game to agree about where
   * that was.
   */
  function fromCanvas(px, py) {
    if (entry.game === 'basketball') {
      const scale = canvas.width / COURT.width;
      return { x: px / scale, y: (canvas.height - 60 - py) / scale };
    }
    if (entry.game === 'refresh') {
      const scale = canvas.width / LANE.width;
      return { x: (px - canvas.width / 2) / scale, y: 0 };
    }
    if (entry.game === 'hands') {
      const scale = canvas.width / PASS.width;
      return { x: px / scale, y: (py - 40) / scale };
    }
    const scale = Math.min((canvas.width - 60) / ROOM.cols, (canvas.height - 110) / ROOM.rows);
    const ox = (canvas.width - ROOM.cols * scale) / 2;
    return { x: (px - ox) / scale, y: (py - 60) / scale };
  }

  function toCanvas(x, y) {
    if (entry.game === 'basketball') {
      const scale = canvas.width / COURT.width;
      return { x: x * scale, y: canvas.height - 60 - y * scale };
    }
    if (entry.game === 'refresh') {
      const scale = canvas.width / LANE.width;
      return { x: canvas.width / 2 + x * scale, y: canvas.height / 2 };
    }
    if (entry.game === 'hands') {
      const scale = canvas.width / PASS.width;
      return { x: x * scale, y: 40 + y * scale };
    }
    const scale = Math.min((canvas.width - 60) / ROOM.cols, (canvas.height - 110) / ROOM.rows);
    const ox = (canvas.width - ROOM.cols * scale) / 2;
    return { x: ox + x * scale, y: 60 + y * scale };
  }

  function step(dt) {
    if (!game || finished) {
      if (game) drawFrame();
      return;
    }

    const at = fromCanvas(pointer.x, pointer.y);
    if (entry.game === 'basketball') game.aimAt(at.x, at.y);
    else if (entry.game === 'refresh') game.moveTo(at.x);
    // Quick Hands takes no pointer position at all. The mouse is one button,
    // held down, and where it is on the pass is nobody's business.
    else if (entry.game !== 'hands') game.moveTo(at.x, at.y);

    game.step(dt);
    drawFrame();

    if (game.done) {
      finished = true;
      showResult();
    }
  }

  function scoreLine() {
    if (entry.game === 'hands') {
      return `${game.taken} away · ${Math.ceil(game.secondsLeft)}s${game.caught ? ' · CAUGHT' : ''}`;
    }
    if (entry.game === 'vacuum') {
      return `${Math.round(game.score * 100)}% of the floor · ${game.taken}/${game.total} · ${Math.ceil(
        game.secondsLeft
      )}s`;
    }
    if (entry.game === 'refresh') {
      return `${game.caught} caught, ${game.dropped} on the floor${game.tipped ? ' · TRAY OVER' : ''}`;
    }
    return `${game.score} in, ${game.shotsLeft} left`;
  }

  function targetLine() {
    if (entry.game === 'vacuum') return `Pass at ${Math.round(row.target * 100)}%`;
    return `Pass at ${row.target}`;
  }

  function drawFrame() {
    frame();
    if (entry.game === 'basketball') drawBasketball();
    else if (entry.game === 'refresh') drawRefresh();
    else if (entry.game === 'hands') drawHands();
    else drawVacuum();
    // A pad has no pointer of its own on screen, so it is drawn: a ring.
    if (padCursor && entry.game !== 'hands') {
      context.strokeStyle = '#f2e6c8';
      context.lineWidth = 2;
      context.beginPath();
      context.arc(pointer.x, pointer.y, 9, 0, Math.PI * 2);
      context.stroke();
    }
    foot.innerHTML = `<span class="class-score">${scoreLine()}</span>
      <span class="class-target">${targetLine()}</span>`;
  }

  function showResult() {
    const passed = game.passed;
    /*
     * The number, the bar, and what it bought. Never a scolding: a failed
     * sitting costs nothing and telling somebody off for a thing that cost them
     * nothing is how an optional system starts feeling compulsory.
     */
    result.hidden = false;
    result.innerHTML = `
      <h3 data-tone="${passed ? 'good' : 'warn'}">${passed ? 'Passed' : 'Not this time'}</h3>
      <p class="class-final">${scoreLine()}</p>
      <p class="class-note">${
        passed
          ? `Level ${row.level} of ${entry.name}. ${sentence(entry.reads)} just got better.`
          : `${targetLine()}. Sit it again while the slot is up.`
      }</p>
      <button class="class-done" type="button">Back to the board</button>`;
    result.querySelector('.class-done').addEventListener('click', () => {
      const done = { entry, row, score: game.score, passed };
      close({ finishing: true });
      onFinish?.(done);
    });
  }

  return {
    root,
    get open() {
      return !root.hidden;
    },
    /** The live minigame, so a headless check can play it rather than mime it. */
    get game() {
      return game;
    },
    get entry() {
      return entry;
    },
    /** The level row being sat. Read by the headless check's own player. */
    get row() {
      return row;
    },
    get finished() {
      return finished;
    },
    close,
    step,

    /**
     * Puts the cursor at a point in the game's own units, as a real event.
     *
     * For the headless check, and deliberately not a back door into the
     * simulation: it works out where on the canvas that point is and then
     * dispatches the mouse move, so the run goes through the same listener, the
     * same bounding box and the same pointer the player's hand does. A script
     * that called `moveTo` on the minigame directly would be testing the
     * minigame and claiming to have tested the screen.
     */
    cursorTo(x, y) {
      if (!game) return null;
      const at = toCanvas(x, y);
      const box = canvas.getBoundingClientRect();
      canvas.dispatchEvent(
        new MouseEvent('mousemove', {
          clientX: box.left + (at.x / canvas.width) * box.width,
          clientY: box.top + (at.y / canvas.height) * box.height,
          bubbles: true,
        })
      );
      return at;
    },

    /**
     * The pad on this screen: the left stick moves the pointer, A is the mouse
     * button (pressed and let go, held for Quick Hands), and A on a finished
     * sitting is the button under the result. B leaves, from `main.js`.
     */
    padStep(state, dt = 1 / 60) {
      if (!game || !state) return;
      const speed = 620;
      if (state.move.x || state.move.y) {
        padCursor = true;
        pointer = {
          x: Math.min(canvas.width, Math.max(0, pointer.x + state.move.x * speed * dt)),
          y: Math.min(canvas.height, Math.max(0, pointer.y - state.move.y * speed * dt)),
        };
      }
      const down = Boolean(state.confirm);
      if (down && !padDown) {
        if (finished) result.querySelector('.class-done')?.click();
        else canvas.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
      }
      if (!down && padDown) canvas.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
      padDown = down;
    },

    /** A click on the canvas, which is the only other thing a hand does here. */
    clickCanvas() {
      canvas.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
    },

    show({ entry: which, row: level, seed }) {
      entry = which;
      row = level;
      finished = false;
      result.hidden = true;
      result.innerHTML = '';
      game = GAMES[which.game]({ row: level, seed });
      pointer = { x: canvas.width / 2, y: canvas.height / 2 };
      head.querySelector('h2').textContent = which.name.toUpperCase();
      head.querySelector('.class-sub').textContent =
        `Level ${level.level} · ${which.where} · ${which.blurb}`;
      root.hidden = false;
      drawFrame();
    },

    /** Ends the sitting where it stands. What the result button does. */
    finish() {
      if (!game) return null;
      const done = { entry, row, score: game.score, passed: game.passed };
      close({ finishing: true });
      onFinish?.(done);
      return done;
    },
  };
}
